import React from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  type FieldArrayRenderProps,
  type FormikErrors,
  type FormikTouched
} from "formik";
import {
  type Config,
  type Cta,
  getDefaultIcon,
  TokenHistoryLabel
} from "kadow-jetpack-model";
import CTASelectedCard from "./CTASelectedCard";
import TSUtils from "../../../../../utils/helpers/TSUtils";
import { createCtaOrderFromCtaList } from "../JetpackCTAEditor";
import getDefaultTitleForCTA from "../../../../../utils/helpers/getDefaultTitleForCTA";
import { isAcquisitionCTA } from "../../../../../utils/helpers/isAcquisitionCTA";

interface DraggableCTAsProps {
  CTAs: Cta[];
  setFieldValue: (field: string, value: any) => void;
  config: Config;
  arrayHelpers: FieldArrayRenderProps;
  errors: FormikErrors<Config>;
  touched: FormikTouched<Config>;
  canRemoveCTA?: boolean;
}

const DraggableCTAs: React.FC<DraggableCTAsProps> = (props) => {
  const addCTA = (
    newCTAType: TokenHistoryLabel,
    isSelected: boolean,
    customId?: string
  ) => {
    if (isSelected) {
      // For custom CTAs with customId, filter by customId
      // For other CTAs, filter by id
      const newCTAs =
        props.config.ctas?.filter((c: Cta) => {
          if (newCTAType === TokenHistoryLabel.Custom && customId) {
            return c.customId !== customId;
          }
          return c.id !== newCTAType;
        }) || [];
      props.setFieldValue("ctas", newCTAs);
    } else {
      const cta: Cta = {
        id: newCTAType,
        title: getDefaultTitleForCTA(newCTAType),
        icon: getDefaultIcon(newCTAType),
        gain: 1,
        nTokensOnPlay: 1,
        v: false
      };
      if (newCTAType === TokenHistoryLabel.Share) {
        cta.sGain = 1;
      }
      const newCTAs = [...(props.config.ctas || []), cta];
      props.setFieldValue("ctas", newCTAs);
    }
  };

  const setCtasOrder = (sourceIndex: number, destIndex: number) => {
    let ctas_order = [...createCtaOrderFromCtaList(props.config)];

    ctas_order = TSUtils.swapArrayElements(ctas_order, sourceIndex, destIndex);
    props.setFieldValue("ctas_order", ctas_order);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) {
      return;
    }

    setCtasOrder(sourceIndex, destIndex);

    props.arrayHelpers.move(sourceIndex, destIndex);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="ctas-list">
        {(provided: any) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="cta-container w-100 d-flex flex-column st-gap"
          >
            {props.CTAs?.map((cta, idx) => {
              if (isAcquisitionCTA(cta.id)) {
                return null;
              }
              // Use customId for custom CTAs to allow multiple instances
              const ctaKey =
                cta.id === TokenHistoryLabel.Custom && cta.customId
                  ? `cta-${cta.id}-${cta.customId}`
                  : `cta-${cta.id}`;
              return (
                <Draggable key={ctaKey} draggableId={ctaKey} index={idx}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <CTASelectedCard
                        cta={cta}
                        idx={idx}
                        setFieldValue={props.setFieldValue}
                        config={props.config}
                        canRemove={props.canRemoveCTA}
                        onToggle={() => {
                          addCTA(cta.id, true, cta.customId);
                        }}
                        isDragging={snapshot.isDragging}
                        errors={props.errors}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableCTAs;
